<script lang="ts" setup>
import Card from 'primevue/card'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import { FloatLabel } from 'primevue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import { Form, FormField } from '@primevue/forms'
import { AVAILABLE_EXCHANGES, QUOTE_TYPES } from '@/utils/consts'
import { i18n } from '@/i18n'
import { watch, ref, useTemplateRef, onMounted } from 'vue'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { z } from 'zod'
import type { FormSubmitEvent } from '@primevue/forms'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import SrDataTable from '@/components/tables/SrDataTable.vue'
import type { FormInstance } from '@primevue/forms'
import { getFavSymbolsColumns } from '@/columns/favSymbolsColumns'
import { useFavSymbolsStore } from '@/stores/favSymbolsStore'
import type { MarketType } from '@/utils/types'
import { capitalize, isNull, isUndefined } from 'lodash'

const symbolsColumns = getFavSymbolsColumns()
const { t } = i18n.global
const loading = ref(true)
const router = useRouter()
const themeStore = useThemeStore()
const favSymbolsStore = useFavSymbolsStore()
const authStore = useAuthStore()
const form = useTemplateRef<FormInstance>('form')
const marketTypes = [
  {
    title: t('spotSwap'),
    value: 'spot-swap',
  },
  {
    title: t('swap'),
    value: 'swap',
  },
  {
    title: t('spot'),
    value: 'spot',
  },
  {
    title: t('swapSpot'),
    value: 'swap-spot',
  },
]
const initValues = {
  exBuy: 'gate',
  exSell: 'mexc',
  marketType: 'swap',
  asset: '',
  sellAsset: '',
  quoteSell: 'USDT',
  quoteBuy: 'USDT',
}

const formattingRules = {
  asset: (v: string) => {
    if (isUndefined(v) || isNull(v)) {
      return ''
    }
    return v
  },
  'ex_buy.name': (v: string) => {
    return capitalize(v)
  },
  'ex_sell.name': (v: string) => {
    return capitalize(v)
  },
  market_type: (v: string) => {
    for (const k of Object.entries(marketTypes)) {
      if (k[1].value === v) {
        return k[1].title
      }
    }
    return ''
  },
}

const resolver = zodResolver(
  z.object({
    asset: z.string().min(1, { message: t('assetEmptyError') }),
    sellAsset: z.string().min(1, { message: t('assetEmptyError') }),
    quoteBuy: z.string().min(1, { message: t('assetEmptyError') }),
    quoteSell: z.string().min(1, { message: t('assetEmptyError') }),
    exBuy: z.string().min(1, { message: t('cexEmptyError') }),
    exSell: z.string().min(1, { message: t('cexEmptyError') }),
    marketType: z.string().min(1, { message: t('marketTypeEmptyError') }),
  }),
)

const onFormSubmit = (e: FormSubmitEvent) => {
  if (!e.valid) {
    return
  }
  loading.value = true
  window.setTimeout(() => {
    let amount = 200
    if (e.values.marketType == 'swap') {
      amount = Number(authStore.settings['amount_usdt_futures'])
    } else if (e.values.marketType == 'spot-swap') {
      amount = Number(authStore.settings['amount_usdt_futures'])
    }
    if (isNaN(amount)) {
      amount = 200
    }

    console.log(e.values)
    const routeData = router.resolve({
      name: 'eye',
      params: {
        asset: e.values.asset.split('/')[0],
        marketType: e.values.marketType,
        exBuy: e.values.exBuy,
        exSell: e.values.exSell,
        amount: amount,
      },
      query: {
        sell: e.values.sellAsset.split('/')[0],
        quoteBuy: e.values.quoteBuy,
        quoteSell: e.values.quoteSell,
      }
    })
    window.open(routeData.href, '_blank')

    loading.value = false
  }, 100)
}

watch(
  () => authStore.currentAccessList,
  (v) => {
    if (!('futures' in v || 'futures_free' in v || 'futures_advanced' in v)) {
      themeStore.forbidden = true
      return
    }
  },
)

const addToFav = async () => {
  const exBuy = form.value?.states.exBuy?.value ?? null
  const exSell = form.value?.states.exSell?.value ?? null
  const marketType = form.value?.states?.marketType?.value ?? null
  const asset = form.value?.states.asset?.value ?? null
  if (exBuy && exSell && asset && marketType) {
    favSymbolsStore.loading.fetch = true
    await favSymbolsStore.pushSymbol(exBuy, exSell, marketType as MarketType, asset)
    await favSymbolsStore.fetchSymbols()
  }
}

const onAssetInput = () => {
  const asset = form.value?.getFieldState("asset")?.value ?? ""
  form.value?.setFieldValue("sellAsset", asset.toUpperCase())
}

onMounted(async () => {
  loading.value = false
  await favSymbolsStore.fetchSymbols()
})
</script>

<template>
  <div>
    <Card>
      <template #subtitle>
        <div class="mb-3">
          <span class="pit success">{{ $t("openNewTab") }}</span>
        </div>
      </template>
      <template #content>
        <Form ref="form" class="grid p-2 mt-1 grid-cols-2 lg:grid-cols-15 gap-3" :initial-values="initValues" :resolver
          @submit="onFormSubmit">
          <!-- Биржа покупки -->
          <FormField v-slot="$field" name="exBuy" class="col-span-2 lg:col-span-2">
            <FloatLabel variant="on">
              <Select :disabled="loading" :loading="loading" fluid :options="AVAILABLE_EXCHANGES" option-label="title"
                option-value="value"></Select>
              <label>{{ $t('exBuy') }}</label>
            </FloatLabel>
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message)
            }}
            </Message>
          </FormField>

          <!-- Биржа продажи -->
          <FormField v-slot="$field" name="exSell" class="col-span-2 lg:col-span-2">
            <FloatLabel variant="on">
              <Select :disabled="loading" :loading="loading" fluid :options="AVAILABLE_EXCHANGES" option-label="title"
                option-value="value"></Select>
              <label>{{ $t('exSell') }}</label>
            </FloatLabel>
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message)
            }}
            </Message>
          </FormField>

          <!-- Тип связки -->
          <FormField v-slot="$field" name="marketType" class="col-span-2 lg:col-span-3">
            <FloatLabel variant="on">
              <Select :disabled="loading" :loading="loading" fluid :options="marketTypes" option-label="title"
                option-value="value"></Select>
              <label>{{ $t('marketType') }}</label>
            </FloatLabel>
            <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">{{ $t($field.error?.message)
            }}
            </Message>
          </FormField>

          <!-- Торговая пара покупки -->
          <div class="relative col-span-2 lg:col-span-3 grid grid-cols-2 gap-1">
            <FormField v-slot="$field" name="asset">
              <FloatLabel variant="on">
                <InputText @input="onAssetInput" :disabled="loading" fluid autocomplete="off" />
                <label>{{ $t('symbol') }}</label>
              </FloatLabel>
              <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">{{
                $t($field.error?.message) }}
              </Message>
            </FormField>
            <FormField name="quoteBuy">
              <FloatLabel variant="on">
                <Select :disabled="loading" fluid :options="QUOTE_TYPES" optionLabel="label" optionValue="value"
                  :placeholder="$t('quoteBuy')" />
                <label> </label>
              </FloatLabel>
            </FormField>
          </div>

          <!-- Торговая пара продажи -->
          <div class="relative col-span-2 lg:col-span-3 grid grid-cols-2 gap-1">
            <FormField v-slot="$field" name="sellAsset">
              <FloatLabel variant="on">
                <InputText :disabled="loading" fluid autocomplete="off" />
                <label>{{ $t('symbol') }}</label>
              </FloatLabel>
              <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">{{
                $t($field.error?.message) }}
              </Message>
            </FormField>
            <FormField name="quoteSell">
              <FloatLabel variant="on">
                <Select :disabled="loading" fluid :options="QUOTE_TYPES" optionLabel="label" optionValue="value"
                  :placeholder="$t('quote_sell')" />
                <label> </label>
              </FloatLabel>
            </FormField>
          </div>

          <div class="lg:col-span-1">
            <Button :loading="loading" :disabled="loading" type="submit" severity="secondary" class="w-full"
              icon="pi pi-link"></Button>
          </div>

          <div class="lg:col-span-1">
            <Button :loading="loading" :disabled="loading" icon="pi pi-star" class="w-full" type="button"
              @click="addToFav" severity="secondary"></Button>
          </div>
        </Form>
      </template>
    </Card>
    <Card class="mt-3" pt:body:class="p-0" style="overflow: hidden">
      <template #subtitle>
        <div class="p-3">
          <span class="pit success">{{ $t("favBunches") }}</span>
        </div>
      </template>
      <template #content>
        <SrDataTable :rows="favSymbolsStore.symbols.length" :columns="symbolsColumns" :col-full="true" size="small"
          :total-records="30" :value="favSymbolsStore.symbols" :loading="favSymbolsStore.loading.fetch" :lazy="false"
          :paginator="false" :formattingRules="formattingRules">
          <template #empty>
            <div class="text-center" style="z-index: 0">{{ $t('emptyMessage') }}</div>
          </template>
        </SrDataTable>
      </template>
    </Card>
  </div>
</template>
